import { clsx } from "clsx";
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";

export type ButtonVariant =
  | "primary"
  | "primaryGreen"
  | "filledDark"
  | "secondary"
  | "secondaryDanger"
  | "secondaryDark"
  | "secondaryLanding"
  | "ghost"
  | "ghostAccent"
  | "underlinedGhost"
  | "underlinedGhostDark"
  | "aiSubmit";

export type ButtonSize = "md" | "sm" | "xs";

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** Render as a full-width block button. */
  fullWidth?: boolean;
  /** HTML button type — distinct from `variant` above. */
  type?: "button" | "submit" | "reset";
}

// Each variant defines: default / hover / disabled states. Using
// `data-*` attributes would be equally valid; we use Tailwind's built-in
// pseudo-class utilities for brevity.
const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: [
    "bg-brandPrimary text-textOnDark",
    "hover:bg-accentCyan800 hover:shadow-buttonHover",
    "disabled:bg-bgDisabled disabled:text-textOnDark disabled:shadow-none",
  ].join(" "),

  primaryGreen: [
    "bg-accentGreen text-textOnDark",
    "hover:bg-accentGreenDark",
    "disabled:bg-bgDisabled disabled:text-textOnDark",
  ].join(" "),

  filledDark: [
    "border border-textDark bg-textDark text-textOnDark",
    "hover:bg-textBody hover:border-textBody",
    "disabled:bg-bgDisabled disabled:border-borderDark disabled:text-textOnDark",
  ].join(" "),

  secondary: [
    "border border-borderDark bg-transparent text-textBody",
    "hover:border-accentCyan800 hover:text-accentCyan800 hover:shadow-buttonHover",
    "disabled:bg-bgDisabled disabled:border-borderDark disabled:text-textOnDark disabled:shadow-none",
  ].join(" "),

  secondaryDanger: [
    "border border-borderDark bg-transparent text-textBody",
    "hover:border-accentError hover:text-accentError",
    "disabled:bg-bgDisabled disabled:border-borderDark disabled:text-textOnDark disabled:shadow-none",
  ].join(" "),

  secondaryDark: [
    "border border-textDark bg-transparent text-textDark font-semibold",
    "hover:border-textBody hover:text-textBody",
    "disabled:bg-bgDisabled disabled:border-borderDark disabled:text-textOnDark",
  ].join(" "),

  secondaryLanding: [
    "border border-brandPrimary bg-bgWhite text-brandPrimary",
    "hover:border-accentGreenDark hover:text-accentGreenDark",
    "disabled:border-bgDisabled disabled:text-bgDisabled",
  ].join(" "),

  ghost: [
    "bg-transparent text-textBody",
    "hover:text-textDark",
    "disabled:text-bgDisabled",
  ].join(" "),

  ghostAccent: [
    "bg-transparent text-accentCyan",
    "hover:text-accentCyan700",
    "disabled:text-bgDisabled",
  ].join(" "),

  underlinedGhost: [
    "bg-transparent text-accentCyan700 underline underline-offset-4",
    "hover:text-accentCyan",
    "disabled:text-bgDisabled",
  ].join(" "),

  underlinedGhostDark: [
    "bg-transparent text-textDark underline underline-offset-4",
    "hover:text-textBody",
    "disabled:text-bgDisabled",
  ].join(" "),

  aiSubmit: [
    "bg-gradientBrand text-textOnDark",
    "hover:brightness-110",
    "disabled:bg-bgDisabled disabled:bg-none disabled:text-textOnDark",
  ].join(" "),
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  md: "h-10 px-6 py-2 text-button rounded-xl gap-2",
  sm: "h-9 px-4 py-2 text-bodySm rounded-[10px] gap-1.5",
  xs: "h-6 px-3 text-bodyXsBold rounded-[10px] gap-1",
};

const GHOST_SIZE_CLASS: Record<ButtonSize, string> = {
  md: "h-10 px-2.5 py-2 text-button rounded-xl gap-2",
  sm: "h-9 px-2 py-2 text-bodySm rounded-[10px] gap-1.5",
  xs: "h-6 px-1.5 text-bodyXsBold rounded-[10px] gap-1",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    leftIcon,
    rightIcon,
    fullWidth = false,
    disabled = false,
    type = "button",
    className,
    children,
    ...rest
  },
  ref,
) {
  const isGhost = variant === "ghost" || variant === "ghostAccent" || variant === "underlinedGhost" || variant === "underlinedGhostDark";
  const sizeClass = isGhost ? GHOST_SIZE_CLASS[size] : SIZE_CLASS[size];

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      className={clsx(
        "inline-flex shrink-0 items-center justify-center whitespace-nowrap transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-accentCyan focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed",
        sizeClass,
        VARIANT_CLASS[variant],
        fullWidth && "w-full",
        className,
      )}
      {...rest}
    >
      {leftIcon ? <span className="inline-flex items-center">{leftIcon}</span> : null}
      {children}
      {rightIcon ? <span className="inline-flex items-center">{rightIcon}</span> : null}
    </button>
  );
});
